/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC } from 'react';
import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  FormControl,
  Heading,
  Icon,
  IconSize,
  Radio,
  Select,
  SideSheetBody,
  SideSheetContentCol,
  SideSheetFooter,
  Text,
  TextFieldAppearance,
  TextFieldSize,
} from '@bechtel/gds';
import { Field, Form, Formik } from 'formik';
import { find, isEqual } from 'lodash';
import { DrawerFooterButtonStyled, DrawerSubHeaderStyled, ListSelectionStyled } from '@bechtel/client-common';
import { BlockDrawerValuesProps, defaultValues, searchByList, searchScopeList, sortByList } from '../form-data';
import BlockStatusList from './block-status';
import { BlockTypeList } from './block-type-list';
import { CommodityTypeList } from './commodity-types-list';
import { Milestones as MilestoneList } from './milestones-list';
import { ButtonBox, DrawerBackdropStyled, DrawerOuterStyled, Header, ResetButtonStyled } from './styled';
import { SortOrderByEnum } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

const SortItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text color="bechtel.colors.grey.1000" size="400" family="Graphik">
      {i18n._(item.label)}
    </Text>
  );
};

interface DrawerProps {
  close: () => void;
  initialValues: BlockDrawerValuesProps;
  onSubmit: (values: BlockDrawerValuesProps) => void;
}

const BlockSearchDrawer: FC<DrawerProps> = ({ close, onSubmit, initialValues }) => {
  const { i18n } = useLingui();
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { resetForm }) => {
        resetForm();
        close();
        onSubmit(values);
      }}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ setFieldValue, values, isValid, dirty, setValues }) => {
        const isAsc = values.orderBy === SortOrderByEnum.ASC;
        return (
          <Form>
            <DrawerBackdropStyled data-testid="block-card-search-canvas-wrap">
              <DrawerOuterStyled>
                <Header>
                  <Box py={{ sm: '400' }}>
                    <Heading size="600" color="grey.1000" weight="600">
                      <Trans>Filters</Trans>
                    </Heading>
                  </Box>
                  <Box py={{ sm: '200' }}>
                    <Button size={ButtonSize.SMALL} appearance={ButtonAppearance.TERTIARY} onClick={close}>
                      <Icon size={IconSize.LARGE} icon="times" fillColor="blue.800" />
                    </Button>
                  </Box>
                </Header>
                <SideSheetBody>
                  <SideSheetContentCol>
                    <Box px={{ sm: '400' }} py={{ sm: '500' }}>
                      <Box mr={{ sm: '400' }} flexGrow={{ sm: 1 }} data-testid="block-card-search-canvas-sort-by">
                        <FormControl style={{ flexGrow: 1 }} label={t`Sort By`}>
                          <Select
                            data-testid="sort-by"
                            size={TextFieldSize.LARGE}
                            appearance={TextFieldAppearance.NORMAL}
                            items={sortByList}
                            itemComponent={SortItem}
                            selectedComponent={SortItem}
                            initialSelectedItem={find(sortByList, { value: values.sortBy })}
                            onSelectedItemChange={(changes: any) => {
                              setFieldValue('sortBy', changes.selectedItem.value);
                            }}
                          />
                        </FormControl>
                      </Box>
                      <Box>
                        <Button
                          appearance={ButtonAppearance.SECONDARY}
                          size={ButtonSize.LARGE}
                          onClick={() => {
                            const newValue = isAsc ? SortOrderByEnum.DESC : SortOrderByEnum.ASC;
                            setFieldValue('orderBy', newValue);
                          }}
                        >
                          <Icon
                            fillColor="blue.800"
                            data-testid="block-card-search-canvas-sort-icon"
                            icon={isAsc ? 'sort-amount-down' : 'sort-amount-up'}
                            size={IconSize.MEDIUM}
                          />
                        </Button>
                      </Box>
                    </Box>
                    <DrawerSubHeaderStyled>
                      <Trans>Work Packages</Trans>
                    </DrawerSubHeaderStyled>
                    {searchScopeList.map((scope) => {
                      return (
                        <ListSelectionStyled
                          key={scope.id}
                          onClick={() => {
                            setFieldValue('searchScope', scope);
                          }}
                        >
                          <Box mr={{ sm: '600' }} data-testid={`block-card-search-canvas-work-package-${scope.id}`}>
                            <Radio checked={values.searchScope.id === scope.id} readOnly />
                          </Box>
                          <Text color="bechtel.colors.grey.1000" size="400">
                            {i18n._(scope.name)}
                          </Text>
                        </ListSelectionStyled>
                      );
                    })}

                    <DrawerSubHeaderStyled>
                      <Trans>Search By</Trans>
                    </DrawerSubHeaderStyled>
                    {searchByList.map((search) => {
                      return (
                        <ListSelectionStyled
                          key={search.id}
                          onClick={() => {
                            setFieldValue('searchBy', search);
                          }}
                        >
                          <Box mr={{ sm: '500' }}>
                            <Radio checked={values.searchBy.value === search.value} readOnly />
                          </Box>
                          <Text color="bechtel.colors.grey.1000" size="400">
                            {i18n._(search.label)}
                          </Text>
                        </ListSelectionStyled>
                      );
                    })}
                    <DrawerSubHeaderStyled>
                      <Trans>Filter By</Trans>
                    </DrawerSubHeaderStyled>
                    <Field name="blockTypeId" component={BlockTypeList} />
                    <Field name="blockStatusList" component={BlockStatusList} />
                    <Field name="commodityTypeId" component={CommodityTypeList} />
                    <Field name="milestoneId" component={MilestoneList} />
                    <ResetButtonStyled>
                      <Button
                        data-testid="block-card-search-canvas-reset"
                        size={ButtonSize.MEDIUM}
                        appearance={ButtonAppearance.SECONDARY}
                        type="reset"
                        disabled={isEqual(defaultValues, values)}
                        onClick={() => {
                          setValues(defaultValues);
                        }}
                      >
                        <Trans>Reset All Filters</Trans>
                      </Button>
                    </ResetButtonStyled>
                  </SideSheetContentCol>
                </SideSheetBody>
                <SideSheetFooter>
                  <ButtonBox>
                    <Box mr={{ sm: '400' }}>
                      <DrawerFooterButtonStyled
                        size={ButtonSize.MEDIUM}
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={close}
                        data-testid="block-card-search-canvas-cancel"
                      >
                        <Trans>Cancel</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                    <Box>
                      <DrawerFooterButtonStyled
                        type="submit"
                        size={ButtonSize.MEDIUM}
                        appearance={ButtonAppearance.SECONDARY}
                        disabled={!dirty || !isValid}
                        data-testid="block-card-search-canvas-apply-filter"
                      >
                        <Trans>Apply Filters</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                  </ButtonBox>
                </SideSheetFooter>
              </DrawerOuterStyled>
            </DrawerBackdropStyled>
          </Form>
        );
      }}
    </Formik>
  );
};

export default BlockSearchDrawer;
